/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import {Typography} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import cloneDeep from 'lodash.clonedeep';
import {resolveDeviceName} from '@ion/core/src/entities/Devices';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {
  createGroup,
  createGroupMembers,
  deleteGroupMembers,
  loadAllGroups
} from '@ion/core/src/services/Connect';
import {
  entityId, setRel
} from '@ion/core/src/services/ConnectUtils';
import {
  AccountLink,
  Assets,
  Device,
  Devices,
  Entity,
  GenericObject,
  GroupMembers
} from '@ion/core/src/types/entities';
import {
  LoadGroupsParams
} from '@ion/core/src/types/services';
import {sortByString} from '@ion/core/src/utils/ArrayUtil';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {SaveButton} from '@ion/dls/src/editors/controls/SaveButton';
import styles, {getClasses} from '@ion/dls/src/editors/editorStyles';
import SelectedEntityChips from '@ion/dls/src/editors/SelectedEntityChips';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {SelectAllSearch} from '@ion/features/src/search/types';
import {useCurrentGlobalGroup} from '../../../hooks/useCurrentGlobalGroup';
import {
  flattenGroupsList,
  getGroupMember,
  getMemberList,
  getUniqGroups,
  GroupActions,
  GroupActionStatus,
  isAddAction,
  isAddNewAction,
  isRemoveAction,
  isReplaceAction,
  isReplaceNewAction
} from './BulkEditGroupUtils';
import GroupActionContainer from './GroupActionContainer';
import {GroupActionList} from './GroupActionList';

export interface BulkEditGroupEditorProps extends SelectAllSearch {
  onCloseDialog?: () => void;
  onRefresh: () => void,
  selected?: Assets | Devices;
  account?: AccountLink;
  entityLabel?: string;
  entity: Entity;
}

export interface BulkActionTypes {
  actionList?: GenericObject[];
  action?: GenericObject;
  index?: number;
  groupMembers?: GroupMembers;
}

const styleOverrides = (theme: DLSTheme) => ({
  formPaper : {
    width                          : '75%',
    [theme.breakpoints.down('sm')] : {
      maxWidth : '100%'
    }
  },
  formDialogContent : {
    [theme.breakpoints.down('sm')] : {
      paddingTop : '0px'
    }
  },
  formSectionTitle : {
    ...dlsAlt('typography', 'cta3'),
    textTransform : 'uppercase',
    padding       : '10px 0'
  },
  flexStyle : {
    display : 'flex'
  }
});

/**
 * The Bulk Edit Group editor with set fields and encapsulated logic
 * for handling the form's state, including validation.
 *
 * @param {Function} onCloseDialog: Callback function fired when the dialog requests to be closed
 * @param {Devices} selectedDevices: selected Devices to perform bulk operation
 */
export const BulkEditGroupEditor = ({
  onCloseDialog,
  onRefresh,
  entityLabel,
  entity,
  account,
  selected,
  search,
  filters,
  areTotalRowsSelected = false
}: BulkEditGroupEditorProps) => {
  const dialogStyles = useExtendStyles(styles, styleOverrides);
  const classes      = dialogStyles();

  const hasMounted                            = useHasMounted();
  const {title: accountName}                  = useCurrentGlobalGroup();
  const [selectedChips, setSelectedChips]     = useState([]);
  const [accountsLoading, setAccountsLoading] = useState<boolean>(false);
  const [isGroupsLoading, setIsGroupsLoading] = useState<boolean>(false);
  const [allGroups, setAllGroups]             = useState([]);
  const [replaceGroups, setReplaceGroups]     = useState([]);
  const [noGroups, setNoGroups]               = useState<boolean>(false);
  const [groupAction, setGroupAction]         = useState<string>(GroupActions.Add);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [actionArray, setActionArray]         = useState([]);
  const [submitError, setSubmitError]         = useState<boolean>(false);
  const [formSubmitted, setFormSubmitted]     = useState<boolean>(false);
  const [disableCancel, setDisableCancel]     = useState<boolean>(false);
  const [showGroupsError, setShowGroupsError] = useState<boolean>(false);

  const isDeviceModule = entity === Entity.device;
  const accountId = entityId(selectedAccount || account);
  const globalAccountId = entityId(account);

  const onChipDelete = useCallback((selectedItem) => {
    const filteredItems = selectedChips?.filter((item) => {
      return item.id !== selectedItem.id;
    });
    setSelectedChips(filteredItems);
    getGroupFromDevices(filteredItems);
  }, [selectedChips]);

  const getGroupFromDevices = useCallback((selectedData: Devices | Assets) => {
    const removeGroupList = [];
    const deviceGroups = flattenGroupsList(selectedData);
    const uniqueGroups = getUniqGroups(deviceGroups);
    uniqueGroups?.forEach(item => {
      removeGroupList.push({
        ...item,
        name : item?.title,
        id   : entityId(item?.href)
      });
    });
    const removeGroupList2 = sortByString(removeGroupList, 'name');
    setReplaceGroups(removeGroupList2);
  }, [groupAction, selectedChips]);

  const checkGroups = useCallback(() => {
    let noGroup = false;
    if (groupAction !== GroupActions.Add) {
      noGroup = !replaceGroups?.length;
    }
    setNoGroups(noGroup);
    setShowGroupsError(true);
  }, [groupAction, replaceGroups]);

  const onDismiss = useCallback(() => {
    setShowGroupsError(false);
  }, []);

  const onSubmit = useCallback(() => {
    setSubmitError(!actionArray?.length);
    if (actionArray?.length && !formSubmitted) {
      const actionList = cloneDeep(actionArray);
      actionList.forEach((action) => {
        action.status = GroupActionStatus.Start;
      });
      setActionArray(actionList);
      processActions(actionList);
    }
  }, [actionArray, formSubmitted]);

  const processActions = async(actionList) => {
    try {
      setFormSubmitted(true);
      setDisableCancel(true);
      const results = [];
      let response;
      for (let i = 0; i < actionList.length; i++) {
        const action = actionList[i];
        const dataFor = action?.dataFor;
        if (isRemoveAction(dataFor)) {
          response = await removeMembersFromGroup(actionList, action, i);
        }
        if (isAddAction(dataFor) || isReplaceAction(dataFor)) {
          response = await addMembersToGroup({actionList, action, index: i});
        }
        if (isAddNewAction(dataFor) || isReplaceNewAction(dataFor)) {
          response = await createNewGroup({actionList, action, index: i});
        }
        results.push(response);
      }
      setDisableCancel(false);
      return results;
    } catch (err) {
      setDisableCancel(false);
    }
  };

  const removeMembersFromGroup = async(actionList, action, index) => {
    const {groupId, dataFor} = action;
    let groupMemberObj;
    if (!areTotalRowsSelected) {
      const membersList = getMemberList(selectedChips);
      groupMemberObj = getGroupMember(isDeviceModule? Entity.device : Entity.asset, membersList, globalAccountId);
    } else {
      groupMemberObj = search;
    }
    if (isRemoveAction(dataFor)) {
      actionList[index].status = GroupActionStatus.InProgress;
      setActionArray(actionList);
    }
    await deleteGroupMembers(groupId, groupMemberObj)
      .then(({errors}) => {
        const isSuccess = !errors?.length;
        actionList[index].status = GroupActionStatus[isSuccess ? 'Success': 'Failed'];
        setActionArray(actionList);
      })
      .catch(err => {
        actionList[index].status = GroupActionStatus.Failed;
        setActionArray(actionList);
        error(err);
      });
  };

  const addMembersToGroup = async({
    actionList,
    action,
    index
  }: BulkActionTypes) => {
    const {groupId, dataFor, toGroupId} = action;
    let groupMemberObj;
    const isReplace = isReplaceAction(dataFor) || isReplaceNewAction(dataFor);
    if (!areTotalRowsSelected) {
      const membersList = getMemberList(selectedChips);
      groupMemberObj = getGroupMember(
        isDeviceModule? Entity.device : Entity.asset,
        membersList, globalAccountId);
    } else {
      groupMemberObj = search;
    }
    if (isAddAction(dataFor) || isReplaceAction(dataFor)) {
      actionList[index].status = GroupActionStatus.InProgress;
      setActionArray(actionList);
    }
    await createGroupMembers(isReplace ? toGroupId : groupId, groupMemberObj).then(() => {
      if (isReplace) {
        removeMembersFromGroup(actionList, action, index);
      } else {
        actionList[index].status = GroupActionStatus.Success;
        setActionArray(actionList);
      }
    }).catch(err => {
      actionList[index].status = GroupActionStatus.Failed;
      setActionArray(actionList);
      error(err);
    });
  };

  const createNewGroup = async({
    actionList,
    action,
    index
  }: BulkActionTypes) => {
    const dataFor = action?.dataFor;
    const isReplaceNew = isReplaceNewAction(dataFor);
    const groupObj = {
      name    : isReplaceNew ? action?.toGroupName : action?.groupName,
      notes   : action?.groupNotes,
      account : setRel(action?.account, 'account')
    };
    actionList[index].status = GroupActionStatus.InProgress;
    setActionArray(actionList);
    await createGroup(groupObj)
      .then((res) => {
        if (isReplaceNew) {
          action.toGroupId = res?.id;
        } else {
          action.groupId = res?.id;
        }
        addMembersToGroup({actionList, action, index});
      })
      .catch(err => {
        actionList[index].status = GroupActionStatus.Failed;
        setActionArray(actionList);
        error(err);
      });
  };

  const onClose = useCallback((event) => {
    onCloseDialog();
    if (formSubmitted) {
      onRefresh();
    }
  }, [formSubmitted]);

  useEffect(() => {
    checkGroups();
  }, [groupAction, replaceGroups]);

  useEffect(() => {
    const devices: Device[] = cloneDeep(selected).map((device: Device) => {
      device.name = resolveDeviceName(device);
      return device;
    });
    setSelectedChips(devices);
    getGroupFromDevices(devices);
  }, [selected]);

  useEffect(() => {
    if (formSubmitted && !disableCancel && actionArray?.length) {
      const actionList = cloneDeep(actionArray);
      actionList.forEach((action) => {
        if (action.status === GroupActionStatus.InProgress) {
          action.status = GroupActionStatus.Success;
        }
      });
      setActionArray(actionList);
    }
  }, [disableCancel, formSubmitted]);

  // whenever the account changes the group are reloaded ...
  useEffect(() => {
    const getGroupsData = async(accountId: number) => {
      setIsGroupsLoading(true);
      let groupsResult;
      try {
        const data: LoadGroupsParams = {
          includeSubGroups : false,
          pageId           : 1,
          pageSize         : 100,
          accountId
        };
        groupsResult = await loadAllGroups(data);
      } catch (err) {
        error(`AdminGroupChooser: Error occurred retrieving Groups for account ${accountId}`);
      } finally {
        if (hasMounted !== false) {
          setIsGroupsLoading(false);
          const groupList = sortByString(groupsResult, 'name');
          setAllGroups(groupList);
        }
      }
    };
    getGroupsData(accountId);
  }, [accountId]);

  return (
    <Dialog open
      onBackdropClick={null}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={classes.formTitle}>
          <div onClick={onClose} className={classes.closeButton}>
            <MaterialExitCloseIcon size={IconSize.medium}
              color={dlsColor('grey3')} />
          </div>
          <Typography component='h1' variant='h2'>
            {translate('admin-groups:edit.bulk.title')}
          </Typography>
        </div>
      </DialogTitle>
      <form className={classes.form} onSubmit={onSubmit}>
        <div className={classes.formWrapperCtc}>
          <DialogContent className={classes.formDialogContent}>
            <>
              <h1 className={classes.formSectionTitle}>
                {`${translate('admin-groups:edit.bulk.selected')} ${entityLabel}`}
              </h1>
              <FormItem fullWidth>
                <SelectedEntityChips noTextTransform
                  selected={selectedChips}
                  filters={filters}
                  entityType={entity}
                  accountName={accountName}
                  areTotalRowsSelected={areTotalRowsSelected}
                  onDelete={onChipDelete} />
              </FormItem>
            </>
            <div className={classes.flexStyle}>
              <GroupActionContainer account={account}
                allGroups={allGroups}
                replaceGroups={areTotalRowsSelected ? allGroups : replaceGroups}
                selectedAccount={selectedAccount}
                setSelectedAccount={setSelectedAccount}
                setAccountsLoading={setAccountsLoading}
                groupAction={groupAction}
                setGroupAction={setGroupAction}
                actionArray={actionArray}
                setActionArray={setActionArray}
                noGroups={areTotalRowsSelected ? false : noGroups}
                isGroupsLoading={isGroupsLoading}
                formSubmitted={formSubmitted} />
              <GroupActionList actionArray={actionArray}
                setActionArray={setActionArray}
                submitError={submitError} />
            </div>
          </DialogContent>
        </div>
        {!areTotalRowsSelected && noGroups && (
          <UserNotificationDialog show={showGroupsError}
            capitalize
            title={translate('admin-groups:edit.bulk.information')}
            message={translate('admin-groups:edit.bulk.noGroupsError', {action: groupAction})}
            dismissLabel={translate('common:ok')}
            onDismiss={onDismiss} />
        )}
      </form>
      <DialogActions className={classes.formActions}>
        {!formSubmitted && (
          <TertiaryButton onClick={onClose}>
            {translate('common:editors.shared.cancel')}
          </TertiaryButton>
        )}
        <SaveButton onSave={!formSubmitted ? onSubmit : onClose}
          label={translate(`common:${formSubmitted ? 'done' : 'submit'}`)}
          loading={disableCancel} />
      </DialogActions>
    </Dialog>
  );
};

export default BulkEditGroupEditor;
